import {
  getTemplateBySlugWithScreensAndFields,
  getTemplateByIdWithScreensAndFields,
  createTemplate,
  createTemplateScreen,
  createTemplateField,
  updateTemplate,
  deleteTemplateScreen,
  deleteTemplateField,
  getTemplates,
} from '@/modules/templates/services/templates.service';
import type { TemplateWithScreens } from '@/modules/templates/types/templates.types';
import type { TemplateExport, TemplateData } from './export-import-schema';
import { templateExportSchema } from './export-import-schema';
import { seedTemplateUilm } from './sync-template-uilm';

function safeJsonParse(value: string | null | undefined): unknown {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

// Caps in-flight requests so a large template can't fire 100s of concurrent UDS mutations.
// Sliding-window: a new task starts the moment a previous one resolves.
const UDS_BATCH_SIZE = 10;
async function runWithConcurrency<T, R>(
  items: readonly T[],
  runner: (item: T) => Promise<R>,
  concurrency = UDS_BATCH_SIZE,
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const i = next++;
      results[i] = await runner(items[i]);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(concurrency, items.length) }, worker),
  );
  return results;
}

/**
 * Export a template (with screens + fields) as a JSON TemplateExport object.
 * Fetches from UDS via the composite service function.
 */
export async function exportTemplate(slug: string): Promise<TemplateExport | null> {
  const template = await getTemplateBySlugWithScreensAndFields(slug);
  if (!template) return null;

  return buildExportData(template);
}

function buildExportData(template: TemplateWithScreens): TemplateExport {
  return {
    version: '1.0',
    exportedAt: new Date().toISOString(),
    template: {
      slug: template.slug,
      title: template.title,
      description: template.description ?? null,
      icon: template.icon ?? null,
      available: template.available ?? false,
      popular: template.popular ?? false,
      href: template.href ?? null,
      keywords: template.keywords ?? [],
      estimatedMinutes: template.estimatedMinutes ?? null,
      systemPrompt: template.systemPrompt ?? null,
      systemPromptRole: template.systemPromptRole ?? null,
      uilmDescriptionKey: template.uilmDescriptionKey ?? null,
      uilmTitleKey: template.uilmTitleKey ?? null,
      screens: template.screens.map((screen) => ({
        title: screen.title,
        description: screen.description ?? null,
        order: screen.order,
        type: (screen.type as 'standard' | 'signatory' | 'dynamic') ?? 'standard',
        aiPrompt: screen.aiPrompt ?? null,
        aiOutputSchema: safeJsonParse(screen.aiOutputSchema as string | null),
        dynamicMaxFields: screen.dynamicMaxFields ?? null,
        dynamicPrompt: screen.dynamicPrompt ?? null,
        signatoryConfig: safeJsonParse(screen.signatoryConfig as string | null),
        uilmDescriptionKey: screen.uilmDescriptionKey ?? null,
        uilmTitleKey: screen.uilmTitleKey ?? null,
        enableApplyStandards: screen.enableApplyStandards ?? false,
        conditions: safeJsonParse(screen.conditions as string | null),
        fields: screen.fields.map((field) => ({
          name: field.name,
          label: field.label,
          type: field.type as any,
          required: field.required,
          placeholder: field.placeholder ?? null,
          helpText: field.helpText ?? null,
          options: field.options ?? [],
          order: field.order,
          aiSuggestionEnabled: field.aiSuggestionEnabled ?? false,
          aiSuggestionKey: field.aiSuggestionKey ?? null,
          uilmHelpTextKey: field.uilmHelpTextKey ?? null,
          uilmLabelKey: field.uilmLabelKey ?? null,
          uilmOptionsKeys: field.uilmOptionsKeys ?? [],
          uilmPlaceholderKey: field.uilmPlaceholderKey ?? null,
          conditions: safeJsonParse(field.conditions as string | null),
        })),
      })),
    },
  };
}

/**
 * Trigger a JSON file download in the browser.
 */
export function downloadTemplateJson(exportData: TemplateExport) {
  const jsonString = JSON.stringify(exportData, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = `${exportData.template.slug}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Import a template from a TemplateExport JSON object.
 * Creates template, screens, and fields via UDS.
 */
export async function importTemplate(
  data: TemplateExport,
): Promise<{ success: boolean; message: string; templateId?: string }> {
  // Validate
  const parsed = templateExportSchema.parse(data);
  const templateData: TemplateData = parsed.template;

  // Check if slug already exists
  const existing = await getTemplates({
    where: { Slug: { eq: templateData.slug } },
    paging: { pageNo: 1, pageSize: 1 },
  });
  if ((existing.items || []).length > 0) {
    return {
      success: false,
      message: `A template with slug "${templateData.slug}" already exists. Please modify the slug in the JSON file before importing.`,
    };
  }

  // Create template
  const templateResult = await createTemplate({
    Slug: templateData.slug,
    Title: templateData.title,
    Description: templateData.description ?? undefined,
    Icon: templateData.icon ?? undefined,
    Available: templateData.available,
    Popular: templateData.popular,
    Href: templateData.href ?? `/templates/${templateData.slug}/generate`,
    Keywords: templateData.keywords,
    EstimatedMinutes: templateData.estimatedMinutes ?? undefined,
    SystemPrompt: templateData.systemPrompt ?? undefined,
    SystemPromptRole: templateData.systemPromptRole ?? undefined,
    UilmDescriptionKey: templateData.uilmDescriptionKey ?? undefined,
    UilmTitleKey: templateData.uilmTitleKey ?? undefined,
  });

  const templateId = templateResult?.insertTemplates?.itemId;
  if (!templateId) {
    return { success: false, message: 'Failed to create template — no ID returned' };
  }

  // Create screens in capped-concurrency batches; field creation must wait until screen IDs are known.
  const screenResults = await runWithConcurrency(templateData.screens, async (screenData) => ({
    result: await createTemplateScreen(buildScreenInput(templateId, screenData)),
    screenData,
  }));

  // Fan out every field across every screen, still capped at UDS_BATCH_SIZE in-flight.
  const fieldTasks = screenResults.flatMap(({ result, screenData }) => {
    const screenId = result?.insertTemplateScreens?.itemId;
    if (!screenId) return [];
    return screenData.fields.map((fieldData) => ({ screenId, fieldData }));
  });
  await runWithConcurrency(fieldTasks, ({ screenId, fieldData }) =>
    createTemplateField(buildFieldInput(screenId, fieldData)),
  );

  // Push the freshly-imported template's keys to UILM. Soft failure:
  // the template is already saved in UDS; UILM can be reconciled later
  // from the Translations tab.
  await trySeed(templateId);

  return { success: true, message: 'Template imported successfully', templateId };
}

/**
 * Wrapper that swallows UILM seed errors. Import / overwrite are about
 * UDS persistence first — UILM is a secondary concern that the admin
 * can repair from the Translations tab if it fails here.
 */
async function trySeed(templateId: string): Promise<void> {
  try {
    await seedTemplateUilm(templateId);
  } catch (err) {
    console.warn('UILM seed deferred for imported/overwritten template:', err);
  }
}

// ============================================================
// Input builders (shared by import + overwrite)
// ============================================================

function buildScreenInput(
  templateId: string,
  screenData: TemplateData['screens'][number],
) {
  return {
    TemplateId: templateId,
    Title: screenData.title,
    Description: screenData.description ?? undefined,
    Order: screenData.order,
    Type: screenData.type,
    AiPrompt: screenData.aiPrompt ?? undefined,
    AiOutputSchema: screenData.aiOutputSchema
      ? JSON.stringify(screenData.aiOutputSchema)
      : undefined,
    DynamicMaxFields: screenData.dynamicMaxFields ?? undefined,
    DynamicPrompt: screenData.dynamicPrompt ?? undefined,
    SignatoryConfig: screenData.signatoryConfig
      ? JSON.stringify(screenData.signatoryConfig)
      : undefined,
    UilmDescriptionKey: screenData.uilmDescriptionKey ?? undefined,
    UilmTitleKey: screenData.uilmTitleKey ?? undefined,
    EnableApplyStandards: screenData.enableApplyStandards,
    Conditions: screenData.conditions ? JSON.stringify(screenData.conditions) : undefined,
  };
}

function buildFieldInput(
  screenId: string,
  fieldData: TemplateData['screens'][number]['fields'][number],
) {
  return {
    ScreenId: screenId,
    Name: fieldData.name,
    Label: fieldData.label,
    Type: fieldData.type,
    Required: fieldData.required,
    Placeholder: fieldData.placeholder ?? undefined,
    HelpText: fieldData.helpText ?? undefined,
    Options: fieldData.options,
    Order: fieldData.order,
    AiSuggestionEnabled: fieldData.aiSuggestionEnabled,
    AiSuggestionKey: fieldData.aiSuggestionKey ?? undefined,
    UilmHelpTextKey: fieldData.uilmHelpTextKey ?? undefined,
    UilmLabelKey: fieldData.uilmLabelKey ?? undefined,
    UilmOptionsKeys: fieldData.uilmOptionsKeys,
    UilmPlaceholderKey: fieldData.uilmPlaceholderKey ?? undefined,
    Conditions: fieldData.conditions ? JSON.stringify(fieldData.conditions) : undefined,
  };
}

export interface OverwriteResult {
  success: boolean;
  message: string;
  summary?: {
    previous: { screens: number; fields: number };
    current: { screens: number; fields: number };
  };
}

/**
 * Overwrite an existing system template's content from a TemplateExport JSON object.
 * - Validates the imported payload against the schema.
 * - Verifies the confirmation slug and ensures the imported slug matches the existing template.
 * - Updates template metadata, deletes existing screens/fields, then recreates them from the payload.
 */
export async function overwriteTemplate(
  templateId: string,
  data: TemplateExport,
  confirmSlug: string,
): Promise<OverwriteResult> {
  const parsed = templateExportSchema.parse(data);
  const importedTemplate: TemplateData = parsed.template;

  const existing = await getTemplateByIdWithScreensAndFields(templateId);
  if (!existing) {
    return { success: false, message: 'Template not found' };
  }

  // SECURITY: only system templates (no OrganizationId) may be overwritten.
  if (existing.organizationId) {
    return { success: false, message: 'Only system templates can be overwritten' };
  }

  if (confirmSlug !== existing.slug) {
    return {
      success: false,
      message: `Confirmation slug does not match. Please type "${existing.slug}" to confirm overwrite.`,
    };
  }

  if (importedTemplate.slug !== existing.slug) {
    return {
      success: false,
      message: `Imported template slug "${importedTemplate.slug}" does not match existing slug "${existing.slug}".`,
    };
  }

  const previousScreenCount = existing.screens.length;
  const previousFieldCount = existing.screens.reduce(
    (acc, screen) => acc + screen.fields.length,
    0,
  );

  // 1. Update template metadata (slug is preserved).
  await updateTemplate(templateId, {
    Title: importedTemplate.title,
    Description: importedTemplate.description ?? '',
    Icon: importedTemplate.icon ?? undefined,
    Available: importedTemplate.available,
    Popular: importedTemplate.popular,
    Href: `/templates/${existing.slug}/generate`,
    Keywords: importedTemplate.keywords,
    EstimatedMinutes: importedTemplate.estimatedMinutes ?? undefined,
    SystemPrompt: importedTemplate.systemPrompt ?? undefined,
    SystemPromptRole: importedTemplate.systemPromptRole ?? undefined,
    UilmDescriptionKey: importedTemplate.uilmDescriptionKey ?? undefined,
    UilmTitleKey: importedTemplate.uilmTitleKey ?? undefined,
  });

  // 2. Delete all existing fields + screens in capped-concurrency batches (hard delete to avoid orphans).
  const deleteTasks: Array<() => Promise<unknown>> = existing.screens.flatMap((screen) => [
    ...screen.fields.map((field) => () => deleteTemplateField(field.id, true)),
    () => deleteTemplateScreen(screen.id, true),
  ]);
  await runWithConcurrency(deleteTasks, (task) => task());

  // 3. Recreate screens in capped-concurrency batches; fields wait until screen IDs are known.
  const screenResults = await runWithConcurrency(importedTemplate.screens, async (screenData) => ({
    result: await createTemplateScreen(buildScreenInput(templateId, screenData)),
    screenData,
  }));

  const fieldTasks = screenResults.flatMap(({ result, screenData }) => {
    const screenId = result?.insertTemplateScreens?.itemId;
    if (!screenId) return [];
    return screenData.fields.map((fieldData) => ({ screenId, fieldData }));
  });
  await runWithConcurrency(fieldTasks, ({ screenId, fieldData }) =>
    createTemplateField(buildFieldInput(screenId, fieldData)),
  );

  const nextScreenCount = importedTemplate.screens.length;
  const nextFieldCount = importedTemplate.screens.reduce(
    (acc, screen) => acc + screen.fields.length,
    0,
  );

  // After overwrite, OLD screen/field ItemIds are gone and NEW ones
  // exist — their keys are missing on UILM. Run seed to fill them.
  // Orphaned old keys stay on UILM and surface in the Translations Tab's
  // Orphans panel for manual cleanup.
  await trySeed(templateId);

  return {
    success: true,
    message: 'Template overwritten successfully',
    summary: {
      previous: { screens: previousScreenCount, fields: previousFieldCount },
      current: { screens: nextScreenCount, fields: nextFieldCount },
    },
  };
}
